import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import StaffingDisplay from '../staffing/StaffingDisplay';

/**
 * UI-015: Manage Caregivers modal opened from a patient profile. Lists the
 * group's SharedLiving caregivers (role 7) with a checkbox to assign / unassign
 * them to this patient.
 *   GET  /athome/caregivers/:patient_id      (currently assigned)
 *   GET  /users/list  (role_id = 7)          (assignable pool)
 *   POST /athome/bulk-assign-caregivers      { patient_id, user_ids[] }
 *   POST /athome/unassign-patient            { patient_id, user_id }
 */
const SharedLivingModal = ({ show, onHide, patientId, patientName = 'Patient', setCaregiverData }) => {
  const [caregivers, setCaregivers] = useState([]);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [loadingIds, setLoadingIds] = useState(new Set());
  // UI-074 / SL-111: /athome/caregivers/:patient_id now returns the patient's
  // nurse + case_manager at the top level of the response. We surface them as
  // a small staffing block above the caregiver toggle table.
  const [nurse, setNurse] = useState(null);
  const [caseManager, setCaseManager] = useState(null);

  useEffect(() => {
    if (show) {
      load();
      setSearch('');
    }
  }, [show]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, assignedRes] = await Promise.all([
        axios.post(`${process.env.REACT_APP_SERVER_URL}/users/list`, {}, { headers }),
        axios.get(`${process.env.REACT_APP_SERVER_URL}/athome/caregivers/${patientId}`, { headers })
      ]);

      const isActive = u => u === true || String(u) === '1' || String(u) === 'true';
      const role7 = (usersRes.data || []).filter(u => Number(u.role_id) === 7 && isActive(u.is_active));
      setCaregivers(
        role7
          .map(u => ({
            id: u.id,
            name: `${u.last_name || ''}, ${u.first_name || ''}`.trim() || u.username,
            email: u.email || '',
            username: u.username
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );

      const assigned = assignedRes.data?.data || assignedRes.data?.caregivers || assignedRes.data || [];
      setAssignedIds(new Set((Array.isArray(assigned) ? assigned : []).map(c => String(c.user_id ?? c.id))));
      setNurse(assignedRes.data?.nurse || null);
      setCaseManager(assignedRes.data?.case_manager || null);
    } catch (error) {
      toast.error('Unable to load caregivers');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return caregivers;
    return caregivers.filter(c =>
      c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)
    );
  }, [caregivers, search]);

  const handleToggle = async caregiver => {
    if (loadingIds.has(caregiver.id)) return;
    const isAssigned = assignedIds.has(String(caregiver.id));

    setLoadingIds(prev => new Set(prev).add(caregiver.id));
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      let arrayOfObjs = []

      if (isAssigned) {
       await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/athome/unassign-patient`,
          { patient_id: patientId, user_id: caregiver.id },
          { headers }
        ).then((res) =>{
          arrayOfObjs = res.data.caregivers
        })
      } else {
        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/athome/bulk-assign-caregivers`,
          { patient_id: patientId, user_ids: [caregiver.id] },
          { headers }
        ).then((res) =>{
          arrayOfObjs = res.data.caregivers
        })
      }

      setAssignedIds(prev => {
        const next = new Set(prev);
        if (isAssigned) next.delete(String(caregiver.id));
        else next.add(String(caregiver.id));
        return next;
      });
      toast.success(
        isAssigned
          ? `Removed ${caregiver.name}`
          : `Assigned ${caregiver.name}`
      );
      setCaregiverData(arrayOfObjs)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to update caregiver assignment');
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(caregiver.id);
        return next;
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">Caregivers — {patientName}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading caregivers…</p>
          </div>
        ) : (
          <>
            {(nurse || caseManager) && (
              <div className="px-3 pt-3">
                <StaffingDisplay nurse={nurse} caseManager={caseManager} />
              </div>
            )}
            <div className="px-3 pt-3 pb-2 border-bottom">
              <InputGroup size="sm">
                <InputGroup.Text>
                  <FontAwesomeIcon icon="search" className="fs-11 text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search caregivers…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </InputGroup>
            </div>
              {filtered.length === 0 ? (
                <Alert variant="info" className="text-center fw-bold mx-3 mt-3">
                  {caregivers.length === 0
                    ? 'No SharedLiving caregivers in this group yet.'
                    : `No caregivers match "${search}"`}
                </Alert>
              ) : (
                <div className='mb-3 p-2'>
                  <Table striped hover size="sm" className="fs-10 mb-0">
                    <thead className="bg-200 text-900">
                      <tr>
                        <th className="text-center px-3" style={{ width: 80 }}>Assigned</th>
                        <th className="px-3">Name</th>
                        <th className="px-3">Email</th>
                        <th className="px-3">Username</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(c => {
                        const isPending = loadingIds.has(c.id);
                        return (
                          <tr key={c.id} className={isPending ? 'opacity-50' : ''}>
                            <td className="text-center px-3 align-middle">
                              {isPending ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                              ) : (
                                <Form.Check
                                  type="checkbox"
                                  checked={assignedIds.has(String(c.id))}
                                  onChange={() => handleToggle(c)}
                                  aria-label={`Toggle ${c.name}`}
                                />
                              )}
                            </td>
                            <td className="px-3 align-middle fw-semibold">{c.name}</td>
                            <td className="px-3 align-middle text-muted">{c.email || '—'}</td>
                            <td className="px-3 align-middle text-muted">{c.username || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SharedLivingModal;
